import React, { useEffect } from 'react';
import { AppBar, Toolbar, Typography, Box, IconButton, Chip, Tooltip, Paper } from '@mui/material';
import { useTheme } from '../contexts/ThemeContext';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCoins } from '../contexts/CoinContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import CountUp from 'react-countup';

const TopBar = ({ onMobileMenuToggle }) => {
  const { darkMode, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { coins, updateCoins, isFlashing } = useCoins();
  const { isActive } = useSubscription();
  
  useEffect(() => {
    updateCoins();
  }, [updateCoins]);

  // Show back button only on detail views, not on main menu items
  const isDetailView = () => {
    const path = location.pathname;
    // Check if we're in a detail view by looking at the URL pattern
    return (
      path.match(/^\/articles\/[^/]+$/) || // Article detail view
      path.match(/^\/sources\/resources\/[^/]+$/) || // Resource detail
      path.match(/^\/sources\/streams\/[^/]+$/)    // Stream detail
    );
  };

  const showBackButton = isDetailView();
  const getBackPath = () => {
    const path = location.pathname;
    if (path.startsWith('/articles/')) return '/articles';
    if (path.startsWith('/sources/resources/')) return '/sources/resources';
    if (path.startsWith('/sources/streams/')) return '/sources/streams';
    return '/';
  };

  const handleBack = () => {
    navigate(getBackPath());
  };

  const estimatesContent = (
    <Paper sx={{ p: 1.5, maxWidth: 280 }}>
      <Typography variant="subtitle2" gutterBottom>
        Estimated capacity with {coins} coins:
      </Typography>
      {[
        {
          model: 'GPT-4o-mini',
          tokens: coins * 6667,
          words: Math.floor(coins * 6667 * 0.75),
          unlimited: isActive
        },
        {
          model: 'GPT-4o',
          tokens: coins * 4000,
          words: Math.floor(coins * 4000 * 0.75)
        }
      ].map((model) => (
        <Box key={model.model} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {model.model}:
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {model.unlimited ? (
              '∞ unlimited'
            ) : (
              <>
                ~<CountUp end={Math.round(model.tokens / 1000)} duration={2} separator="," />K tokens
              </>
            )}
          </Typography>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {model.unlimited ? (
              '∞ unlimited'
            ) : (
              <>
                ~<CountUp end={Math.round(model.words / 1000)} duration={2} separator="," />K words
              </>
            )}
          </Typography>
        </Box>
      ))}
    </Paper>
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar>
        {showBackButton ? (
          <IconButton 
            color="inherit" 
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
        ) : (
          <IconButton
            color="inherit"
            edge="start"
            onClick={onMobileMenuToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
          <Typography variant="h6" component="div">
            Writellect✨
          </Typography>
          <Typography
            component="div"
            variant="caption"
            sx={{
              opacity: 0.7,
              bgcolor: 'action.selected',
              px: 1,
              py: 0.1,
              borderRadius: 1,
              userSelect: 'none',
              fontSize: '0.7rem',
              display: 'inline-block',
              mt: { xs: 0, sm: 0 },
              ml: { xs: 0, sm: 1 }
            }}
          >
            Alpha v0.1
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Tooltip 
            title={estimatesContent}
            arrow
            placement="bottom-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'background.paper',
                  '& .MuiTooltip-arrow': {
                    color: 'background.paper',
                  },
                }
              }
            }}
          >
            <Chip
              icon={<MonetizationOnIcon />}
              label={`${coins} coins`}
              color="secondary"
              variant="outlined"
              onClick={() => navigate('/settings?active_tab=usage')}
              sx={{ 
                borderColor: 'inherit',
                '& .MuiChip-icon': { color: 'inherit' },
                color: 'inherit',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8
                },
                transition: 'all 0.3s ease-in-out',
                ...(isFlashing && {
                  backgroundColor: 'secondary.main',
                  color: 'secondary.contrastText',
                  transform: 'scale(1.1)',
                  '& .MuiChip-icon': {
                    color: 'secondary.contrastText'
                  }
                })
              }}
            />
          </Tooltip>
          <IconButton
            color="inherit"
            onClick={toggleTheme}
          >
            {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar; 